import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import { Divider, Switch, Alert } from "@mui/material";
import AudioPlayerForRouting from "../reusables/AudioPlayerForRouting";
import TooltipComponent from "../reusables/TooltipComponent";
import RouteSelect from "../reusables/RouteSelect";
import {
  getForwardNumberOptions,
  resolveForwardNumberSelectValue,
} from "../../utils/forwardNumberChannels";
import {
  FORWARD_MODE_DYNAMIC,
  FORWARD_MODE_SIMPLE,
  shouldShowForwardRecording,
} from "../../utils/forwardActionMode";
import { DYNAMIC_FORWARD_BACKUP_NONE } from "../../hooks/useCallAction";
import { MAX_FORWARD_DESTINATIONS } from "../../utils/forwardDestinations";
import {
  isDynamicForwardAvailable,
  isMultiForwardAvailable,
} from "../../config/callActionOptions";
import { useRouteConfigModals } from "../contexts/RouteConfigModalsContext";
import { useDynamicForwardUpgrade } from "../../hooks/useDynamicForwardUpgrade";
import DynamicForwardUpgradeModal from "../modals/DynamicForwardUpgradeModal";

const MULTI_FORWARD_UPGRADE_MESSAGE = (
  <>
    Simultaneous destinations ring multiple numbers at once (first answer wins).
    Upgrade to the <strong>Team Plan</strong> to use this feature.
  </>
);

const FORWARD_DOCS = {
  [FORWARD_MODE_SIMPLE]: {
    href: "https://www.twilanswer.com/docs/how-to-forward-calls-in-twilanswer",
    description: "Always ring the number you choose.",
  },
  [FORWARD_MODE_DYNAMIC]: {
    href: "https://www.twilanswer.com/docs/how-to-use-dynamic-forward-in-twilanswer",
    description: "Ring the contact's assigned user, with a backup if no one is assigned.",
  },
};

const DEFAULT_CALLER_ID_OPTIONS = [
  { value: "caller", label: "Show Original Caller's Number" },
  { value: "twilio", label: "Show System Number" },
];

/**
 * Unified Forward Call UI — Simple | Dynamic selector + Twilanswer VM toggle.
 * Maps onto stored types forward / forwardvwm / dynamic_forward.
 */
const ForwardUI = ({
  mode = FORWARD_MODE_SIMPLE,
  numberValue,
  destinationValues = null,
  callerIdPref,
  callApproval,
  recordingValue,
  vmFallback = false,
  handleNumberChange,
  handleDestinationsChange,
  handleCallerIdChange,
  handleCallApprovalChange,
  handleRecordingChange,
  handleVmFallbackChange,
  handleModeChange,
  containerClass,
  headerSize,
  showDivider,
  setOpenConfigureSlotModal,
  channels = [],
  recordings = [],
  callerIdOptions = null,
  hasValidationError = false,
  addDestinationOption,
  addRecordingOption,
  onDestinationChange,
  /** Optional: (index) => addOption for a specific destination row (multi-forward). */
  getAddDestinationOption,
}) => {
  const planDetails = useRouteConfigModals()?.planDetails;
  const isPlanLocked =
    mode === FORWARD_MODE_DYNAMIC &&
    planDetails != null &&
    !isDynamicForwardAvailable(planDetails);
  const { requestUpgrade, upgradeOpen, goToBilling, cancelUpgrade } =
    useDynamicForwardUpgrade();
  const [upgradeFeature, setUpgradeFeature] = useState("dynamic");

  const requestDynamicUpgrade = () => {
    setUpgradeFeature("dynamic");
    requestUpgrade();
  };
  const requestMultiUpgrade = () => {
    setUpgradeFeature("multi");
    requestUpgrade();
  };

  const effectiveCallerIdOptions = callerIdOptions ?? DEFAULT_CALLER_ID_OPTIONS;
  const defaultCallerId =
    mode === FORWARD_MODE_DYNAMIC
      ? "twilio"
      : effectiveCallerIdOptions[0]?.value ?? "caller";
  const defaultCallApproval =
    mode === FORWARD_MODE_DYNAMIC ? "enabled" : "disabled";
  const isNonDefaultCallerId =
    callerIdPref !== defaultCallerId && callerIdPref !== "";
  const showCallApproval =
    mode === FORWARD_MODE_DYNAMIC || !!vmFallback;
  const hasNonDefaultAdvanced =
    isNonDefaultCallerId ||
    (showCallApproval && callApproval !== defaultCallApproval);
  const [advancedOpen, setAdvancedOpen] = useState(() => hasNonDefaultAdvanced);
  /** Extra empty picker row while the user is adding another simultaneous destination. */
  const [pendingExtraSlot, setPendingExtraSlot] = useState(false);

  const showRecording = shouldShowForwardRecording({
    mode,
    numberValue,
    vmFallback,
  });
  const backupIsNone =
    !numberValue || numberValue === DYNAMIC_FORWARD_BACKUP_NONE;

  useEffect(() => {
    if (hasNonDefaultAdvanced) setAdvancedOpen(true);
  }, [hasNonDefaultAdvanced]);

  useEffect(() => {
    setPendingExtraSlot(false);
  }, [mode]);

  useEffect(() => {
    if (planDetails != null && !isMultiForwardAvailable(planDetails)) {
      setPendingExtraSlot(false);
    }
  }, [planDetails]);

  const forwardOptions = useMemo(
    () => getForwardNumberOptions(channels),
    [channels]
  );

  const destinationOptions = useMemo(() => {
    if (mode === FORWARD_MODE_DYNAMIC) {
      return [
        { value: DYNAMIC_FORWARD_BACKUP_NONE, label: "None — send to voicemail" },
        ...forwardOptions,
      ];
    }
    return forwardOptions;
  }, [mode, forwardOptions]);

  const destinationValue = useMemo(() => {
    if (mode === FORWARD_MODE_DYNAMIC && backupIsNone) {
      return DYNAMIC_FORWARD_BACKUP_NONE;
    }
    return resolveForwardNumberSelectValue(
      numberValue,
      channels,
      destinationOptions
    );
  }, [mode, backupIsNone, numberValue, channels, destinationOptions]);

  /** Saved phones for simple simultaneous forward (no empty placeholders). */
  const savedSimpleDestinations = useMemo(() => {
    if (mode !== FORWARD_MODE_SIMPLE) return [];
    return Array.isArray(destinationValues)
      ? destinationValues.filter((p) => p != null && String(p).trim() !== "")
      : [];
  }, [mode, destinationValues]);

  /** Rows shown in the UI: saved phones + optional empty pending row. */
  const simpleDestinationSlots = useMemo(() => {
    if (mode !== FORWARD_MODE_SIMPLE) return [];
    if (savedSimpleDestinations.length === 0) return [""];
    const allowPending =
      pendingExtraSlot &&
      savedSimpleDestinations.length < MAX_FORWARD_DESTINATIONS &&
      (planDetails == null || isMultiForwardAvailable(planDetails));
    if (allowPending) {
      return [...savedSimpleDestinations, ""];
    }
    return savedSimpleDestinations;
  }, [mode, savedSimpleDestinations, pendingExtraSlot, planDetails]);

  const recordingOptions = useMemo(() => {
    if (!recordings) return [];
    return recordings.map((item) => ({
      value: item.M.rec_id.S,
      label: item.M.title.S,
    }));
  }, [recordings]);

  const recordingUrl = recordingValue
    ? recordings?.find((e) => e.M.rec_id.S === recordingValue)?.M.url.S
    : null;

  const handleDestinationSelect = (selected) => {
    if (selected === DYNAMIC_FORWARD_BACKUP_NONE) {
      (onDestinationChange ?? handleNumberChange)(DYNAMIC_FORWARD_BACKUP_NONE);
      return;
    }
    (onDestinationChange ?? handleNumberChange)(selected);
  };

  const commitSimpleDestinations = (nextPhones) => {
    const cleaned = nextPhones.filter((p) => p != null && String(p).trim() !== "");
    setPendingExtraSlot(false);
    if (handleDestinationsChange) {
      handleDestinationsChange(cleaned);
      return;
    }
    handleNumberChange?.(cleaned[0] ?? null);
  };

  const handleSimpleDestinationSelect = (index, selected) => {
    if (onDestinationChange) {
      onDestinationChange(selected, index, savedSimpleDestinations);
      setPendingExtraSlot(false);
      return;
    }
    const next = [...savedSimpleDestinations];
    if (index < next.length) {
      next[index] = selected;
    } else {
      next.push(selected);
    }
    commitSimpleDestinations(next);
  };

  const handleRemoveDestination = (index) => {
    const next = savedSimpleDestinations.filter((_, i) => i !== index);
    commitSimpleDestinations(next);
  };

  const multiForwardAllowed =
    planDetails == null || isMultiForwardAvailable(planDetails);
  /** Existing 2+ destinations on a non-Team plan: keep Remove, lock edits. */
  const isLegacyMultiLocked =
    mode === FORWARD_MODE_SIMPLE &&
    planDetails != null &&
    !isMultiForwardAvailable(planDetails) &&
    savedSimpleDestinations.length > 1;

  const handleAddDestinationSlot = () => {
    if (savedSimpleDestinations.length >= MAX_FORWARD_DESTINATIONS) return;
    if (savedSimpleDestinations.length === 0) return;
    if (!multiForwardAllowed) {
      requestMultiUpgrade();
      return;
    }
    setPendingExtraSlot(true);
  };

  const canAddDestination =
    mode === FORWARD_MODE_SIMPLE &&
    savedSimpleDestinations.length > 0 &&
    savedSimpleDestinations.length < MAX_FORWARD_DESTINATIONS &&
    !pendingExtraSlot &&
    !isLegacyMultiLocked;

  const optionsForSimpleSlot = (slotIndex) => {
    const currentPhone = simpleDestinationSlots[slotIndex] || "";
    const currentSelect = resolveForwardNumberSelectValue(
      currentPhone,
      channels,
      forwardOptions
    );
    const takenSelectValues = new Set(
      savedSimpleDestinations
        .map((phone, i) => {
          if (i === slotIndex) return null;
          return resolveForwardNumberSelectValue(phone, channels, forwardOptions);
        })
        .filter(Boolean)
    );
    return forwardOptions.filter(
      (opt) => opt.value === currentSelect || !takenSelectValues.has(opt.value)
    );
  };

  const handleModeSelect = (nextMode) => {
    if (nextMode === FORWARD_MODE_DYNAMIC) {
      if (planDetails != null && !isDynamicForwardAvailable(planDetails)) {
        requestDynamicUpgrade();
        return;
      }
    }
    handleModeChange?.(nextMode);
  };

  return (
    <div>
      <div
        className={`${containerClass}${hasValidationError ? " !bg-red-50/80" : ""}`}
      >
        {/* Header */}
        <div className="flex flex-nowrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center min-w-0 flex-1">
            <ArrowRight className="text-green-500 shrink-0 mr-2" />
            <div className="min-w-0">
              <h2 className={`${headerSize} text-gray-800`}>Forward Call</h2>
              <div className="text-[13px]">
                Send the call to another number or the contact's assigned user
              </div>
            </div>
          </div>
          {setOpenConfigureSlotModal && (
            <button
              type="button"
              className="shrink-0 whitespace-nowrap text-sm text-blue-600 font-medium hover:underline text-[13px]"
              onClick={() => setOpenConfigureSlotModal(true)}
            >
              ↻ Change
            </button>
          )}
        </div>
        {showDivider && <Divider />}

        {/* Simple | Dynamic selector */}
        <div
          className="mb-2 flex w-full items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5"
          role="tablist"
          aria-label="Forward mode"
        >
          {[
            { id: FORWARD_MODE_SIMPLE, label: "Simple Forward" },
            { id: FORWARD_MODE_DYNAMIC, label: "Dynamic Forward" },
          ].map((tab) => {
            const active = mode === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => handleModeSelect(tab.id)}
                className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  active
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <p className="mb-4 text-[12px] text-gray-500">
          {FORWARD_DOCS[mode].description}{" "}
          <a
            href={FORWARD_DOCS[mode].href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary font-medium hover:underline"
          >
            Learn more
          </a>
        </p>

        {isPlanLocked && (
          <Alert color="warning" sx={{ mb: 3 }}>
            <div
              className="text-[14px] hover:underline hover:text-blue-600 cursor-pointer"
              onClick={requestDynamicUpgrade}
            >
              Upgrade to the Team Plan to use Dynamic Forward
            </div>
          </Alert>
        )}

        <div className={isPlanLocked ? "opacity-60 pointer-events-none" : ""}>
          {/* Destination(s) */}
          <div className="mb-4">
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-medium text-gray-700">
                {mode === FORWARD_MODE_DYNAMIC
                  ? "Backup Forwarding Destination"
                  : savedSimpleDestinations.length > 1
                    ? "Destinations (simultaneous)"
                    : "Destination"}
              </span>
              <TooltipComponent
                message={
                  mode === FORWARD_MODE_DYNAMIC
                    ? "Used when there is no matching contact or the contact has no assignee"
                    : "Numbers ring at the same time; the first to answer takes the call"
                }
              />
            </div>
            {mode === FORWARD_MODE_DYNAMIC ? (
              <RouteSelect
                value={destinationValue}
                onChange={handleDestinationSelect}
                options={destinationOptions}
                placeholder="Select a backup destination"
                addOption={addDestinationOption}
              />
            ) : (
              <div className="space-y-2">
                {isLegacyMultiLocked && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    <div className="text-[13px]">
                      Simultaneous destinations require Team.{" "}
                      <button
                        type="button"
                        className="text-brand-primary font-medium hover:underline"
                        onClick={requestMultiUpgrade}
                      >
                        Upgrade
                      </button>{" "}
                      to edit, or remove extras to stay on your plan.
                    </div>
                  </Alert>
                )}
                {simpleDestinationSlots.map((phone, index) => {
                  const slotOptions = optionsForSimpleSlot(index);
                  const selectValue = resolveForwardNumberSelectValue(
                    phone,
                    channels,
                    slotOptions
                  );
                  const rowAddOption = isLegacyMultiLocked
                    ? undefined
                    : getAddDestinationOption?.(index) ??
                      (index === 0 ? addDestinationOption : undefined);
                  return (
                    <div key={`dest-${index}`} className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <RouteSelect
                          value={selectValue}
                          onChange={(selected) =>
                            handleSimpleDestinationSelect(index, selected)
                          }
                          options={slotOptions}
                          placeholder={
                            index === 0
                              ? "Select a destination"
                              : "Select another destination"
                          }
                          addOption={rowAddOption}
                          disabled={isLegacyMultiLocked}
                        />
                      </div>
                      {savedSimpleDestinations.length > 1 && index < savedSimpleDestinations.length && (
                        <button
                          type="button"
                          className="shrink-0 text-sm text-gray-500 hover:text-red-600 px-1"
                          onClick={() => handleRemoveDestination(index)}
                          aria-label={`Remove destination ${index + 1}`}
                        >
                          Remove
                        </button>
                      )}
                      {pendingExtraSlot &&
                        index === simpleDestinationSlots.length - 1 &&
                        !phone && (
                          <button
                            type="button"
                            className="shrink-0 text-sm text-gray-500 hover:text-gray-800 px-1"
                            onClick={() => setPendingExtraSlot(false)}
                            aria-label="Cancel adding destination"
                          >
                            Cancel
                          </button>
                        )}
                    </div>
                  );
                })}
                {canAddDestination && (
                  <button
                    type="button"
                    className="text-sm text-brand-primary font-medium hover:underline"
                    onClick={handleAddDestinationSlot}
                  >
                    + Add another destination
                  </button>
                )}
                {savedSimpleDestinations.length > 1 && !isLegacyMultiLocked && (
                  <Alert
                    severity={vmFallback ? "info" : "warning"}
                    sx={{ mt: 1, py: 0.5 }}
                  >
                    <div className="text-[13px]">
                      {vmFallback ? (
                        <>
                          Numbers ring at once (first answer wins). Twilanswer
                          Voicemail Fallback and Call Approval are on so an
                          unanswered call lands in your business voicemail —
                          not a personal cellphone mailbox.
                        </>
                      ) : (
                        <>
                          With multiple destinations, a personal cellphone
                          voicemail can answer first. Turn on Twilanswer
                          Voicemail Fallback (and Call Approval) so unanswered
                          calls stay in Twilanswer.
                        </>
                      )}
                    </div>
                  </Alert>
                )}
              </div>
            )}
          </div>

          {/* Twilanswer Voicemail Fallback */}
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium text-gray-700">
                  Twilanswer Voicemail Fallback
                </span>
                <TooltipComponent
                  message={
                    mode === FORWARD_MODE_DYNAMIC
                      ? "When enabled, unanswered calls play a Twilanswer voicemail instead of the user's voicemail"
                      : savedSimpleDestinations.length > 1
                        ? "Recommended with multiple destinations: rings stop after ~13s and play your Twilanswer greeting instead of a personal cellphone voicemail"
                        : "When enabled, unanswered calls play a Twilanswer voicemail instead of the destination's phone voicemail"
                  }
                />
              </div>
              <p className="text-[12px] text-gray-500 mt-1">
                {mode === FORWARD_MODE_DYNAMIC
                  ? "Play Twilanswer voicemail when the assignee does not answer"
                  : savedSimpleDestinations.length > 1
                    ? "Stops the ring after ~13s and plays your Twilanswer greeting if nobody answers"
                    : "Play Twilanswer voicemail when the destination does not answer"}
              </p>
            </div>
            <Switch
              checked={!!vmFallback}
              onChange={(e) => handleVmFallbackChange?.(e.target.checked)}
              color="primary"
            />
          </div>

          {/* Recording */}
          {showRecording && (
            <div className="mb-4">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-sm font-medium text-gray-700">
                  Recording
                </span>
                <TooltipComponent
                  message={
                    mode === FORWARD_MODE_DYNAMIC && backupIsNone
                      ? "Voicemail message to play when no contact or assignee is found"
                      : "Select the voicemail message to play if the call isn't answered"
                  }
                />
              </div>
              <RouteSelect
                value={recordingValue}
                onChange={handleRecordingChange}
                options={recordingOptions}
                placeholder="Select a recording"
                addOption={addRecordingOption}
              />
              {recordingUrl && (
                <div className="mt-3">
                  <AudioPlayerForRouting src={recordingUrl} />
                </div>
              )}
            </div>
          )}

          {/* Advanced Settings */}
          <div>
            <button
              type="button"
              onClick={() => setAdvancedOpen((prev) => !prev)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              {advancedOpen ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              <span>Advanced Settings</span>
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-4">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-sm font-medium text-gray-700">
                      Caller ID Setting
                    </span>
                    <TooltipComponent message="Choose which caller ID to see when the call is forwarded" />
                  </div>
                  <RouteSelect
                    value={callerIdPref}
                    onChange={handleCallerIdChange}
                    options={effectiveCallerIdOptions}
                    placeholder="Select preference"
                  />
                </div>
                {showCallApproval && (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        Call Approval
                      </span>
                      <TooltipComponent message="When enabled, you'll hear who is calling and can press 1 to connect or let it go to voicemail" />
                    </div>
                    <RouteSelect
                      value={callApproval}
                      onChange={handleCallApprovalChange}
                      options={[
                        {
                          value: "disabled",
                          label: "Disabled - Connect Immediately",
                        },
                        {
                          value: "enabled",
                          label: "Enabled - Press 1 to connect",
                        },
                      ]}
                      placeholder="Select"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <DynamicForwardUpgradeModal
        open={upgradeOpen}
        onUpgrade={goToBilling}
        onCancel={cancelUpgrade}
        message={
          upgradeFeature === "multi"
            ? MULTI_FORWARD_UPGRADE_MESSAGE
            : undefined
        }
      />
    </div>
  );
};

export default ForwardUI;
